import { useState } from "react";

import JSONInput from "react-json-editor-ajrm";
import { locale } from "../../../rule-engine-core/Common/JsonInputLocale";

import { DataDefinition } from "../../../rule-engine-core/Common/Data/DataDefinition";

import { DataStructureDifferences } from "../../../rule-engine-core/Common/Data/ExecutionData/DataStructureDifferences";

import { StateManager } from "../../../rule-engine-core/Common/StateManager";
import { Box, Button } from "@mui/material";
import { EditModal } from "./EditModal";
import { EditIcon } from "../../Icon/EditIcon";

import { DataUtilities } from "../../../rule-engine-core/Common/DataUtilities";
import { CompareChangesLayer } from "./CompareChangesLayer";
import { OpenAIChatLink } from "../../OpenAI/OpenAIChatLink";
import { UIStyleConstants } from "../../../rule-engine-core/Common/UIStyleConstants";

export const JSONUIComponent = (props: { definition: DataDefinition }) => {
    const [openJSONModal, setOpenJSONModal] = useState<boolean>(false);
    const [applyError, setApplyError] = useState<string | undefined>();
    const [dataChanges, setDataChanges] = useState<DataStructureDifferences[]>([]);
    const [updatedDef, setUpdatedDef] = useState<DataDefinition | undefined>();
    const [value, setValue] = useState(props.definition.value);

    const onToggleJSONModal = () => {
        setOpenJSONModal(!openJSONModal);
    };

    const onCancel = () => {
        setDataChanges([]);
        setUpdatedDef(undefined);
        setOpenJSONModal(false);
    };

    const onApply = () => {
        setValue(value);
        if (!updatedDef) {
            onCancel();
            return;
        }
        props.definition.fields = updatedDef.fields;
        props.definition.value = updatedDef.value;
        StateManager.changed(props.definition);

        setDataChanges([]);
        setUpdatedDef(undefined);
        setOpenJSONModal(false);
    };

    return (
        <Box
            display={"flex"}
            flexDirection={"column"}
            width={"100%"}
        >
            <Box
                display={"flex"}
                flexDirection={"row"}
                marginBottom={"15px"}
                alignItems={"center"}
                justifyContent={"space-between"}
                width={"100%"}
            >
                <Button
                    variant="contained"
                    sx={{ background: UIStyleConstants.BLUE_COLOR }}
                    startIcon={<EditIcon />}
                    onClick={onToggleJSONModal}
                >
                    Edit JSON
                </Button>
                {props.definition.fields.length === 0 && (
                    <OpenAIChatLink
                        linkName={"Generate JSON"}
                        title={"Generate JSON"}
                        onSelectAnswer={(v) => {
                            setApplyError(undefined);
                            try {
                                props.definition.applyOwner();

                                let value = DataUtilities.parseJSON(v);

                                let tempDef = new DataDefinition();
                                tempDef.fromJSONObject(value);

                                let changes = props.definition.detectDataStructureChanges(tempDef.fields, true);
                                if (changes.length > 0) {
                                    setDataChanges(changes);
                                    setUpdatedDef(tempDef);
                                } else if (!DataUtilities.compare(value, JSON.parse(props.definition.toJSONObject()))) {
                                    props.definition.value = value;
                                    setValue(value);
                                    setUpdatedDef(undefined);
                                    setOpenJSONModal(false);
                                    StateManager.changed(props.definition);
                                } else {
                                    setOpenJSONModal(false);
                                }
                            } catch (error) {
                                setApplyError(error.message);
                            }
                        }}
                        pretext={"Generate JSON for"}
                    ></OpenAIChatLink>
                )}

                {props.definition.fields.length !== 0 && (
                    <OpenAIChatLink
                        linkName={"Generate Test Data"}
                        title={"Generate Test Data"}
                        needsClean={(t: string) => {
                            try {
                                JSON.parse(t);
                            } catch {
                                return true;
                            }
                            return false;
                        }}
                        onSelectAnswer={(v) => {
                            setApplyError(undefined);
                            try {
                                props.definition.applyOwner();

                                let value = DataUtilities.parseJSON(v);

                                let tempDef = new DataDefinition();
                                tempDef.fromJSONObject(value);

                                let changes = props.definition.detectDataStructureChanges(tempDef.fields, true);
                                if (changes.length > 0) {
                                    setDataChanges(changes);
                                    setUpdatedDef(tempDef);
                                } else if (!DataUtilities.compare(value, JSON.parse(props.definition.toJSONObject()))) {
                                    props.definition.value = value;
                                    setUpdatedDef(undefined);
                                    setOpenJSONModal(false);
                                    setValue(value);
                                } else {
                                    props.definition.value = value;
                                    setOpenJSONModal(false);
                                }
                            } catch (error) {
                                setApplyError(error.message);
                            }
                        }}
                        initialPrompt={"Generate JSON Object for Schema: " + props.definition.toJSONSchema()}
                    ></OpenAIChatLink>
                )}
            </Box>
            <JSONInput
                placeholder={value}
                locale={locale}
                theme={"light_mitsuketa_tribute"}
                style={{
                    outerBox: {
                        width: "100%",
                    },
                    container: {
                        width: "100%",
                    },
                }}
                viewOnly
            />
            <EditModal
                open={openJSONModal}
                value={props.definition.toJSONObject()}
                error={applyError}
                onEdit={(value: object) => {
                    setApplyError(undefined);
                    try {
                        props.definition.applyOwner();

                        let tempDef = new DataDefinition();
                        tempDef.fromJSONObject(value);

                        let changes = props.definition.detectDataStructureChanges(tempDef.fields, true);
                        if (changes.length > 0) {
                            setDataChanges(changes);
                            setUpdatedDef(tempDef);
                        } else if (!DataUtilities.compare(value, JSON.parse(props.definition.toJSONObject()))) {
                            props.definition.value = value;
                            setUpdatedDef(undefined);
                            setOpenJSONModal(false);
                        } else {
                            setOpenJSONModal(false);
                        }
                    } catch (error) {
                        setApplyError(error.message);
                    }
                }}
                onClose={() => setOpenJSONModal(false)}
            />
            {dataChanges.length > 0 && (
                <CompareChangesLayer
                    title={"JSON Object Structure Compare"}
                    oldValue={props.definition.toStructureString(0)}
                    newValue={updatedDef!.toStructureString(0)}
                    onApply={onApply}
                    onCancel={onCancel}
                />
            )}
        </Box>
    );
};
